import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { 
  Users, 
  ShieldCheck, 
  Key, 
  Lock, 
  CheckCircle2, 
  XCircle, 
  Plus, 
  History, 
  Clock, 
  UserCheck,
  AlertTriangle
} from 'lucide-react';

export default function AdminStaff() {
  const { storeSettings, addToast } = useShop();

  const [staffMembers, setStaffMembers] = useState([
    {
      id: 'STF-01',
      name: 'Ayush Soni',
      email: storeSettings.adminEmail || 'admin@mljewellers.com',
      role: 'Super Admin',
      status: 'Active',
      lastLogin: 'Today, 02:15 PM IST',
      twoFactor: 'Enforced (OTP / Hardware Token)'
    },
    {
      id: 'STF-02',
      name: 'Rajesh Sharma',
      email: 'rajesh.vault@mljewellers.com',
      role: 'Inventory & Vault Manager',
      status: 'Active',
      lastLogin: 'Yesterday, 06:40 PM IST',
      twoFactor: 'Enforced (SMS OTP)'
    },
    {
      id: 'STF-03',
      name: 'CA Suresh Agarwal',
      email: 'accounts@mljewellers.com',
      role: 'Accountant & Tax Auditor',
      status: 'Active',
      lastLogin: '08 Oct 2026, 11:20 AM IST',
      twoFactor: 'Enforced (Email 2FA)'
    },
    {
      id: 'STF-04',
      name: 'Priyanka Verma',
      email: 'sales.concierge@mljewellers.com',
      role: 'Order & Customer Concierge',
      status: 'Active',
      lastLogin: 'Today, 10:30 AM IST',
      twoFactor: 'Enforced'
    }
  ]);

  const permissionsMatrix = [
    { module: 'Dashboard & Revenue Analytics', superAdmin: true, inventory: false, accountant: true, sales: false },
    { module: 'Live Gold / Silver Rates Override', superAdmin: true, inventory: false, accountant: false, sales: false },
    { module: 'Product & Pricing Management', superAdmin: true, inventory: true, accountant: false, sales: false },
    { module: 'Vault Physical Inventory & Stock In', superAdmin: true, inventory: true, accountant: false, sales: false },
    { module: 'Customer Database & CRM Dossier', superAdmin: true, inventory: false, accountant: false, sales: true },
    { module: 'GST Invoices & GSTR-1 Tax Filing', superAdmin: true, inventory: false, accountant: true, sales: false },
    { module: 'Database Backups & Cloud Sync', superAdmin: true, inventory: false, accountant: false, sales: false }
  ];

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Top Banner */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '1.25rem 1.6rem',
        border: '1px solid #e0e0e0',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2874f0', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.2rem' }}>
            <ShieldCheck size={14} />
            <span>ACCESS CONTROL & SECURITY AUDIT</span>
          </div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#212121', margin: 0 }}>
            Staff Roles & Security Permissions
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#616161' }}>
            Multi-tier role delegation, Two-Factor Authentication (2FA), activity session logs & vault safeguards.
          </p>
        </div>

        <button
          onClick={() => addToast?.('Invite Staff modal opened', 'info')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem',
            padding: '0.55rem 1rem',
            borderRadius: '4px',
            border: 'none',
            background: '#2874f0',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.82rem',
            cursor: 'pointer'
          }}
        >
          <Plus size={15} />
          <span>Add Staff Member</span>
        </button>
      </div>

      {/* Staff Directory */}
      <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden', marginBottom: '1.5rem' }}>
        <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid #f0f0f0', fontWeight: 800, color: '#1e293b', fontSize: '0.95rem' }}>
          Authorized Showroom & Vault Operators
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '0.74rem', textTransform: 'uppercase' }}>
              <th style={{ padding: '0.8rem 1rem' }}>Staff Name</th>
              <th style={{ padding: '0.8rem 1rem' }}>Designated Role</th>
              <th style={{ padding: '0.8rem 1rem' }}>2FA Security</th>
              <th style={{ padding: '0.8rem 1rem' }}>Last Active Session</th>
              <th style={{ padding: '0.8rem 1rem' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {staffMembers.map(s => (
              <tr key={s.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.8rem 1rem' }}>
                  <div style={{ fontWeight: 800, color: '#1e293b' }}>{s.name}</div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{s.email}</div>
                </td>
                <td style={{ padding: '0.8rem 1rem' }}>
                  <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '0.15rem 0.5rem', borderRadius: '3px', fontWeight: 700, fontSize: '0.72rem' }}>
                    {s.role}
                  </span>
                </td>
                <td style={{ padding: '0.8rem 1rem', color: '#065f46', fontWeight: 600 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Lock size={12} color="#15803d" /> {s.twoFactor}
                  </span>
                </td>
                <td style={{ padding: '0.8rem 1rem', color: '#64748b' }}>{s.lastLogin}</td>
                <td style={{ padding: '0.8rem 1rem' }}>
                  <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.15rem 0.45rem', borderRadius: '3px', fontWeight: 700, fontSize: '0.7rem' }}>
                    ● {s.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Role-Based Permissions Matrix */}
      <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
        <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1e293b', margin: '0 0 1rem' }}>
          Role-Based Access Control (RBAC) Matrix
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
              <th style={{ padding: '0.7rem 1rem' }}>Admin System Capability</th>
              <th style={{ padding: '0.7rem 1rem', textAlign: 'center' }}>Super Admin</th>
              <th style={{ padding: '0.7rem 1rem', textAlign: 'center' }}>Vault & Inventory</th>
              <th style={{ padding: '0.7rem 1rem', textAlign: 'center' }}>Accountant (CA)</th>
              <th style={{ padding: '0.7rem 1rem', textAlign: 'center' }}>Sales Concierge</th>
            </tr>
          </thead>
          <tbody>
            {permissionsMatrix.map(p => (
              <tr key={p.module} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#334155' }}>{p.module}</td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                  {p.superAdmin ? <CheckCircle2 size={16} color="#15803d" style={{ margin: '0 auto' }} /> : <XCircle size={16} color="#94a3b8" style={{ margin: '0 auto' }} />}
                </td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                  {p.inventory ? <CheckCircle2 size={16} color="#15803d" style={{ margin: '0 auto' }} /> : <XCircle size={16} color="#94a3b8" style={{ margin: '0 auto' }} />}
                </td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                  {p.accountant ? <CheckCircle2 size={16} color="#15803d" style={{ margin: '0 auto' }} /> : <XCircle size={16} color="#94a3b8" style={{ margin: '0 auto' }} />}
                </td>
                <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                  {p.sales ? <CheckCircle2 size={16} color="#15803d" style={{ margin: '0 auto' }} /> : <XCircle size={16} color="#94a3b8" style={{ margin: '0 auto' }} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
}
